import { Component, Output, Input, ViewChild, AfterViewInit, ElementRef, EventEmitter } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { distinctUntilChanged, catchError, toArray, concatMap } from 'rxjs/operators';
import { Observable, from, Observer, of } from 'rxjs';

import { cropperDialogConfig, invalidExtensionErrorKey, invalidFilekey, invalidSizeKey } from '@app/core/utils/media-management';
import { ImageViewerComponent } from '@app/core/components/image-cropper/image-viewer.component';
import { IUploadType, IUploadedFile, IUploadError, IFileViewerResult } from '@app/api/models/media.models';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  standalone: false,
})
export class FileUploadComponent implements AfterViewInit {
  @ViewChild('fileInput') fileInput: ElementRef;
  @Input() previewMode: boolean;
  @Input() googlePostMode: boolean;
  @Input() uploadTitle: string;
  @Input() uploadMessage: string;
  @Input() uploadType: IUploadType;
  @Input() googlePostImagesCount: number;
  @Input() disabled = false;

  @Output() uploadedFiles = new EventEmitter<IUploadedFile[]>();
  uploadTypes = IUploadType;
  imgURL;

  constructor(
    private imageCropper: MatDialog,
    private translate: TranslateService
  ) {}

  /**
   * After view initializes, the uploadType is checked. If it's not of the type LOGO,
   * the "multiple" property of the fileInput is set to true
   */
  ngAfterViewInit() {
    if (this.fileInput && this.uploadType === IUploadType.IMAGE) {
      this.fileInput.nativeElement.multiple = true;
    }
  }

  uploadFile(uploadedFiles) {
    const files = this.validateFileExtensions(Array.from(uploadedFiles));

    from(files)
      .pipe(
        concatMap(file => this.loadImage(file).pipe(catchError((error: IUploadedFile) => of(error)))),
        toArray()
      )
      .subscribe(fileArray => {
        this.checkUploadTypeAndSave(fileArray);
      });
  }

  /**
   * Validates each file extension.
   *
   * Any file that doesn't match the extension regex is immediatly emitted to the parent component with the error property set to true
   *
   * @param uploadedFiles files
   * @return returns an array of Files that have a valid extension
   */
  private validateFileExtensions(uploadedFiles): File[] {
    const validatedFiles = new Array<File>();
    const invalidFiles = new Array<IUploadedFile>();
    for (const file of uploadedFiles) {
      if (this.isValidExtension(file.type)) {
        if (this.isValidSize(file.size)) {
          validatedFiles.push(file);
        } else {
          invalidFiles.push({ error: this.buildError(file.name, invalidSizeKey), file });
        }
      } else {
        invalidFiles.push({ error: this.buildError(file.name, invalidExtensionErrorKey), file });
      }
    }

    this.uploadedFiles.emit(invalidFiles);
    return validatedFiles;
  }

  private isValidSize(size: number): boolean {
    const toKByte = size / 1024;
    return toKByte >= 5 && toKByte <= 5120;
  }

  private isValidExtension(mimeType: string): boolean {
    return mimeType.match(/image\/*/) !== null;
  }

  /**
   * Reads a file and tries to create an Image object out of it.
   * Emits an observable with the uploadedFile object, which holds the file, image and error (if it exists)
   *
   * @param file uploaded file
   */
  private loadImage(file): Observable<any> {
    const imgReader = new FileReader();
    const image = new Image();
    return Observable.create((observer: Observer<any>) => {
      imgReader.readAsDataURL(file);
      imgReader.onload = _event => {
        this.imgURL = imgReader.result;
        const imgObj = {
          name: file.name,
          url: this.imgURL,
        };
        image.onload = () => {
          observer.next({ image: imgObj, file });
          observer.complete();
        };

        image.onerror = () => {
          observer.error({ file, error: this.buildError(file.name, invalidFilekey) });
        };
        image.src = this.imgURL;
      };
      imgReader.onerror = () => {
        observer.error({ file, error: this.buildError(file.name, invalidFilekey) });
      };
    });
  }

  /**
   * Checks the file upload type. If it's the LOGO type, then it opens the Cropper Component
   * @param uploadedFiles Array of objects that hold the respective file, image and error
   */
  private checkUploadTypeAndSave(uploadedFiles: IUploadedFile[]): void {
    this.fileInput.nativeElement.value = '';
    if (uploadedFiles && this.uploadType === IUploadType.LOGO) {
      this.openCropper(uploadedFiles);
    } else {
      this.uploadedFiles.emit(uploadedFiles);
    }
  }

  /**
   * Checks if the logo file has an error. If it has, the file is emmitted to the parent component which will warn the user.
   * If the file is valid, the cropper is opened
   *
   * @param uploadedFiles Array of objects that hold the respective file, image and error
   */
  private openCropper(uploadedFiles: IUploadedFile[]): void {
    const fileToCrop: IUploadedFile = uploadedFiles[0];
    const { name } = fileToCrop.file;
    if (!fileToCrop.error) {
      this.imageCropper
        .open(ImageViewerComponent, cropperDialogConfig(fileToCrop.image, IUploadType.LOGO, true))
        .afterClosed()
        .pipe(distinctUntilChanged())
        .subscribe((result: IFileViewerResult) => {
          const { image } = result;
          if (image) {
            this.uploadedFiles.emit([{ file: new File([image], name, { type: fileToCrop.file.type }) }]);
          }
        });
    } else {
      this.uploadedFiles.emit(uploadedFiles);
    }
  }

  inputClick(): void {
    if (this.disabled) {
      return;
    }
    this.fileInput.nativeElement.click();
  }

  private buildError(fileName: string, messageKey: string): IUploadError {
    return { fileName, errorMessage: this.translate.instant(messageKey) };
  }
}
